import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { retryJsonFetch, RetryOptions } from '@/utils/retryHelper';
import { safeJsonParse } from '@/utils/apiUtils';
import { HeartPulse, Moon, Dumbbell, BarChart3, User, Ruler } from '@/utils/icons';

interface WhoopAuthResponse {
    auth_url: string;
    state: string;
    scopes: string[];
    redirect_uri: string;
}

interface WhoopConnectionStatus {
    connected: boolean;
    last_sync?: string;
    token_valid?: boolean;
}

/**
 * ConnectWhoop component handles the complete OAuth 2.0 flow for Whoop integration.
 * Uses environment variables for OAuth credentials and redirects users to Whoop's authorization page.
 */
const ConnectWhoop: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [authResponse, setAuthResponse] = useState<WhoopAuthResponse | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<WhoopConnectionStatus | null>(null);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);

    const scopes = [
        { name: 'read:recovery', description: 'HRV and recovery data', Icon: HeartPulse },
        { name: 'read:sleep', description: 'Sleep tracking and quality', Icon: Moon },
        { name: 'read:workout', description: 'Activity and workout data', Icon: Dumbbell },
        { name: 'read:cycles', description: 'Physiological cycle data', Icon: BarChart3 },
        { name: 'read:profile', description: 'Basic profile information', Icon: User },
        { name: 'read:body_measurement', description: 'Body measurements', Icon: Ruler }
    ];

    useEffect(() => {
        // Check if user is logged in
        const token = localStorage.getItem('access');
        if (!token) {
            // Store the intended destination for after login
            sessionStorage.setItem('redirectAfterLogin', '/connect-whoop');
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }

        // Only check connection status if authenticated
        checkConnectionStatus();
    }, [navigate]);

    const checkConnectionStatus = async () => {
        try {
            setCheckingStatus(true);
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            // Retry options optimized for AWS networking
            const retryOptions: RetryOptions = {
                maxAttempts: 3,
                initialDelay: 500,
                maxDelay: 5000,
                timeout: 15000, // 15 second timeout
                retryCondition: (error) => {
                    // Retry on network errors and 5xx server errors
                    return error.status >= 500 ||
                           error.message.includes('fetch') ||
                           error.message.includes('timeout') ||
                           error.name === 'AbortError';
                }
            };

            const result = await retryJsonFetch<WhoopConnectionStatus>(
                getApiUrl('/api/users/whoopstatus/'),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                },
                retryOptions
            );

            if (result.success && result.data) {
                setConnectionStatus(result.data);

                // Auto-refresh if token is expired
                if (result.data.connected && !result.data.token_valid) {
                    setSuccess('Token expired - automatically refreshing...');
                    setTimeout(() => {
                        refreshToken();
                    }, 2000); // Small delay to show the status first
                }

            } else {
                console.error('Failed to check connection status after retries:', result.error);

                // Set a default disconnected status if all retries failed
                setConnectionStatus({
                    connected: false,
                    token_valid: false
                });

                // Show user-friendly error if it took multiple attempts
                if (result.attempts > 1) {
                    setError(`Connection check failed after ${result.attempts} attempts. Please check your internet connection.`);
                }
            }
        } catch (error) {
            console.error('Unexpected error checking connection status:', error);

            // Set a default disconnected status on unexpected error
            setConnectionStatus({
                connected: false,
                token_valid: false
            });

            setError('Unable to check connection status. Please try again.');
        } finally {
            setCheckingStatus(false);
        }
    };

    /**
     * Generate a cryptographically secure random state parameter
     * for OAuth CSRF protection
     */
    const generateRandomState = () => {
        const array = new Uint8Array(16);
        window.crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    };

    const initiateOAuthFlow = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                setError('Authentication required. Please log in again.');
                navigate('/login');
                return;
            }

            // Retry options optimized for OAuth flow initiation
            const retryOptions: RetryOptions = {
                maxAttempts: 4, // More attempts for critical OAuth flow
                initialDelay: 1000,
                maxDelay: 8000,
                timeout: 20000, // 20 second timeout for OAuth setup
                retryCondition: (error) => {
                    // Retry on network errors, timeouts, and server errors
                    // Don't retry on 4xx client errors (except 429 rate limiting)
                    if (error.status >= 400 && error.status < 500 && error.status !== 429) {
                        return false;
                    }
                    return error.status >= 500 ||
                           error.status === 429 ||
                           error.message.includes('fetch') ||
                           error.message.includes('timeout') ||
                           error.name === 'AbortError';
                }
            };

            const result = await retryJsonFetch<WhoopAuthResponse>(
                getApiUrl('/api/whoop/auth/'),
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({}),  // No state needed - backend generates it
                },
                retryOptions
            );

            if (result.success && result.data) {
                setAuthResponse(result.data);

                // Store state from backend for callback verification
                localStorage.setItem('whoop_oauth_state', result.data.state);

                // Small delay before redirect to ensure state is saved
                setTimeout(() => {
                    window.location.href = result.data!.auth_url;
                }, 100);

            } else {

                // Provide more detailed error messages
                let errorMessage = result.error || 'Failed to initiate OAuth flow';

                if (result.attempts > 1) {
                    errorMessage = `Connection failed after ${result.attempts} attempts. ${errorMessage}`;
                }

                // Check for common AWS/network issues
                if (result.error?.includes('timeout')) {
                    errorMessage += ' The server is taking too long to respond. Please check your connection and try again.';
                } else if (result.error?.includes('fetch')) {
                    errorMessage += ' Network connection issue detected. Please check your internet connection.';
                }

                setError(errorMessage);
            }
        } catch (error) {
            // Clean up state on unexpected error
            localStorage.removeItem('whoop_oauth_state');

            console.error('Unexpected OAuth initiation error:', error);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Retry connection with exponential backoff
     */
    const retryConnection = async () => {
        setIsRetrying(true);
        setError('');
        setRetryCount(prev => prev + 1);

        // Clear any cached connection status to force a fresh check
        setConnectionStatus(null);

        try {
            // Wait a moment before retrying to avoid overwhelming the server
            await new Promise(resolve => setTimeout(resolve, 1000));

            await checkConnectionStatus();

        } catch (error) {
            console.error(`Connection retry #${retryCount + 1} failed:`, error);
        } finally {
            setIsRetrying(false);
        }
    };

    const disconnectWhoop = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                setError('Authentication required. Please log in again.');
                navigate('/login');
                return;
            }

            const response = await fetch(getApiUrl('/api/whoop/disconnect/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await safeJsonParse(response, 'disconnecting Whoop');

            if (result.success) {
                // Update connection status to reflect disconnection
                setConnectionStatus(prev => ({
                    connected: false,
                    last_sync: prev?.last_sync,
                    token_valid: false
                }));

                // OAuth state cleanup
                localStorage.removeItem('whoop_oauth_state');

                // Show success message
                setSuccess('Whoop account disconnected successfully!');
                setError(''); // Clear any previous errors

            } else {
                setError(result.error || 'Failed to disconnect Whoop account');
            }
        } catch (error) {
            setError('Network error occurred. Please try again.');
            console.error('Disconnect error:', error);
        } finally {
            setLoading(false);
        }
    };

    const refreshToken = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                setError('Authentication required. Please log in again.');
                navigate('/login');
                return;
            }
            const response = await fetch(getApiUrl('/api/whoop/refresh/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await safeJsonParse(response, 'refreshing Whoop token');

            if (result.success) {
                setSuccess('Token refreshed successfully!');
                await checkConnectionStatus();
            } else {
                setError(result.error || 'Failed to refresh token. Please reconnect.');
            }
        } catch (error) {
            setError('Network error occurred. Please try again.');
            console.error('Token refresh error:', error);
        } finally {
            setLoading(false);
        }
    };

    if (checkingStatus) {
        return (
            <div className="min-h-screen flex flex-col px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">

                {/* Main Content */}
                <div className="flex-1 flex items-center justify-center py-8">
                    <div className="flex flex-col items-center space-y-4">
                        <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                            <span className="cgm-text-primary">Checking Whoop connection status...</span>
                        </div>
                        <button
                            onClick={checkConnectionStatus}
                            className="text-sm cgm-text-secondary hover:cgm-text-primary transition-colors"
                        >
                            Click here if this takes too long
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">


            <div className="max-w-3xl w-full space-y-6">
                <div className="flex flex-col items-center">
                    <img
                        src={isDark ? "/logos/whoop/Logo/Wordmark/WHOOP Logo White.svg" : "/logos/whoop/Logo/Wordmark/WHOOP Logo Black.svg"}
                        alt="WHOOP Logo"
                        style={{ minWidth: 100, maxWidth: 200 }}
                    />
                </div>
                {/* Header */}
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Your Whoop Account
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Securely connect your Whoop account using OAuth 2.0 to access your HRV,
                        recovery, sleep, and activity data.
                    </p>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">
                        <div className="flex flex-col space-y-3">
                            <div>{error}</div>

                            {/* Retry Button for Network Errors */}
                            {(error.includes('Connection') || error.includes('Network') || error.includes('timeout') || error.includes('fetch')) && (
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2 text-xs cgm-text-secondary">
                                        <span>Retry attempt: {retryCount}</span>
                                        {retryCount > 0 && (
                                            <span>• Last retry: {new Date().toLocaleTimeString()}</span>
                                        )}
                                    </div>
                                    <button
                                        onClick={retryConnection}
                                        disabled={isRetrying || loading}
                                        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                            isRetrying || loading
                                                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                : 'bg-red-600 hover:bg-red-700 text-white'
                                        }`}
                                    >
                                        {isRetrying ? (
                                            <div className="flex items-center">
                                                <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent mr-2"></div>
                                                Retrying...
                                            </div>
                                        ) : (
                                            `Retry Connection${retryCount > 0 ? ` (${retryCount})` : ''}`
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Success Display */}
                {success && (
                    <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">
                        {success}
                    </div>
                )}

                {/* Connection Status */}
                {connectionStatus ? (
                    <div className="cgm-card p-6 mb-4">
                        <div className="flex items-center gap-4 mb-2">
                            <h3 className="text-lg font-medium cgm-text-primary">Current Status</h3>
                            <span className={`px-3 py-1 rounded-full text-sm ${
                                !connectionStatus?.connected
                                    ? 'cgm-alert-error'
                                    : !connectionStatus?.token_valid
                                    ? 'cgm-alert-warning'
                                    : 'cgm-alert-success'
                                }`}
                            >
                                {!connectionStatus?.connected
                                    ? 'Not Connected'
                                    : !connectionStatus?.token_valid
                                    ? 'Token Expired'
                                    : 'Connected'
                                }
                            </span>
                        </div>
                        {connectionStatus.last_sync && (
                            <p className="text-sm cgm-text-muted mb-8">
                                Last sync: {new Date(connectionStatus.last_sync).toLocaleString()}
                            </p>
                        )}
                        <div className="flex gap-3 justify-center flex-wrap">
                            {!connectionStatus?.connected ?
                                (
                                    <>
                                        <button
                                            onClick={initiateOAuthFlow}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            {loading ? 'Connecting...' : 'Connect with Whoop'}
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </>
                                ) : !connectionStatus?.token_valid ? (
                                    <>
                                        <button
                                            onClick={refreshToken}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            {loading ? 'Refreshing...' : 'Refresh Token'}
                                        </button>
                                        <button
                                            onClick={disconnectWhoop}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            {loading ? 'Disconnecting...' : 'Disconnect'}
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={disconnectWhoop}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            {loading ? 'Disconnecting...' : 'Disconnect'}
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            disabled={loading}
                                            className="cgm-button px-4 py-2"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </>
                                )
                            }
                        </div>
                    </div>
                ) : (
                    <div className="cgm-card p-6 mb-4">
                        <div className="text-center">
                            <h3 className="text-lg font-medium cgm-text-primary mb-4">Connection Status Unavailable</h3>
                            <p className="cgm-text-secondary mb-4 text-sm">
                                Unable to check your Whoop connection status. You can still try to connect.
                            </p>
                            <div className="flex gap-3 justify-center flex-wrap">
                                <button
                                    onClick={initiateOAuthFlow}
                                    disabled={loading}
                                    className="cgm-button px-4 py-2"
                                >
                                    {loading ? 'Connecting...' : 'Connect with Whoop'}
                                </button>
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    disabled={loading}
                                    className="cgm-button px-4 py-2"
                                >
                                    Go to Dashboard
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data Scopes */}
                <div className="cgm-card p-6 mb-4">
                    <h3 className="text-lg font-medium cgm-text-primary">Data Access Permissions</h3>
                    <p className="text-sm cgm-text-muted mb-3">
                        Your Whoop account will grant access to the following data:
                    </p>
                    <div className="space-y-2">
                        {scopes.map((scope) => (
                            <div key={scope.name} className="flex items-center p-2">
                                <span className="mr-3"><scope.Icon className="w-5 h-5 cgm-text-primary" /></span>
                                <div>
                                    <p className="cgm-text-primary text-sm">{scope.description}</p>
                                    <p className="text-xs cgm-text-muted">Scope: {scope.name}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Security Note */}
                <div className="cgm-card rounded-lg p-4 transition-all duration-500 ease-in-out">
                    <p className="text-sm cgm-text-secondary">
                        <strong>Security:</strong> You'll be redirected to Whoop's official website
                        to log in with your credentials. We never see your Whoop username or password.
                        Your access tokens are securely encrypted and can be revoked at any time
                        through your Whoop account settings.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ConnectWhoop;
